import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import InteractiveAtom from "@/components/InteractiveAtom";

export const Hero = () => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative isolate overflow-hidden grain"
        >
            {/* Background organic blobs */}
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/30 dark:bg-[hsl(var(--brand-copper))]/20"
                style={{ width: 480, height: 480, top: -160, right: -120 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/30 dark:bg-[hsl(var(--brand-moss))]/25"
                style={{ width: 520, height: 520, bottom: -200, left: -160 }}
            />

            <div className="container-x relative z-10 pt-40 md:pt-48 pb-20 md:pb-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    {/* Left content */}
                    <div className="lg:col-span-7">
                        <div className="reveal inline-flex items-center gap-2 rounded-full border border-border/80 bg-background/50 backdrop-blur-md px-4 py-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            <span className="overline !text-[11px]">
                                Sustainable biotechnology solutions
                            </span>
                        </div>

                        <h1
                            data-testid="hero-title"
                            className="reveal reveal-delay-1 mt-7 font-display font-medium leading-[0.98] text-5xl sm:text-6xl md:text-7xl tracking-tighter"
                        >
                            Building tomorrow's
                            <span className="block">
                                <em className="not-italic text-primary">bio-innovations</em>
                            </span>
                            <span className="block">today.</span>
                        </h1>

                        <p
                            data-testid="hero-subtitle"
                            className="reveal reveal-delay-2 mt-7 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground"
                        >
                            Yaalee Biotech Solutions integrates biology, chemistry and
                            engineering to develop sustainable products that work with
                            nature — for a better future.
                        </p>

                        <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href="#solutions"
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-md transition hover:bg-primary/90"
                            >
                                Explore our solutions
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#why"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-2 rounded-full border border-border bg-background/40 backdrop-blur-md px-7 py-3.5 text-sm font-medium hover:border-primary/60 transition"
                            >
                                Why Yaalee?
                            </a>
                        </div>

                        {/* Stat row */}
                        <div className="reveal reveal-delay-4 mt-14 grid grid-cols-3 max-w-lg gap-6">
                            {[
                                { k: "3", v: "scientific disciplines" },
                                { k: "100%", v: "sustainability-first" },
                                { k: "∞", v: "rural impact" },
                            ].map((s, i) => (
                                <div
                                    key={i}
                                    data-testid={`hero-stat-${i}`}
                                    className="border-l border-border pl-4"
                                >
                                    <div className="font-display text-3xl md:text-4xl font-medium text-foreground">
                                        {s.k}
                                    </div>
                                    <div className="text-[11px] mt-1 uppercase tracking-[0.18em] text-muted-foreground">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right - interactive atom */}
                    <div className="lg:col-span-5 reveal reveal-delay-2">
                        <InteractiveAtom />
                    </div>
                </div>
            </div>

            {/* Subtle marquee at bottom */}
            <div className="relative z-10 border-y border-border/60 py-6 overflow-hidden bg-background/40 backdrop-blur-sm">
                <div className="flex whitespace-nowrap gap-12 marquee-track text-muted-foreground font-display text-2xl md:text-3xl">
                    {Array.from({ length: 2 }).map((_, idx) => (
                        <div key={idx} className="flex gap-12 items-center pr-12">
                            {[
                                "Bio-innovation",
                                "·",
                                "Process optimization",
                                "·",
                                "Waste-to-value",
                                "·",
                                "Rural empowerment",
                                "·",
                                "Sustainable manufacturing",
                                "·",
                                "Custom R&D",
                                "·",
                            ].map((w, i) => (
                                <span key={`${idx}-${i}`} className="italic font-medium">
                                    {w}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Hero;
